import { useEffect, useState, type FormEvent } from "react";
import { api, type YutaiItem } from "../lib/api.js";
import { AuthGate } from "../components/AuthGate.js";
import { ErrorText, Field, stateLabel } from "../components/ui.js";

export function YutaiPage() {
  return (
    <AuthGate>
      <YutaiInner />
    </AuthGate>
  );
}

const searchLinks = (item: YutaiItem) => {
  const q = encodeURIComponent(`${item.stockName ?? item.stockCode} 優待`);
  return {
    yahoo: `https://auctions.yahoo.co.jp/search/search?p=${q}`,
    mercari: `https://jp.mercari.com/search?keyword=${q}`,
  };
};

const daysUntil = (iso: string | null): number | null => {
  if (!iso) return null;
  const today = new Date();
  const t = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const [y, m, d] = iso.split("-").map(Number);
  return Math.round((Date.UTC(y!, m! - 1, d!) - t) / 86_400_000);
};

function YutaiInner() {
  const [items, setItems] = useState<YutaiItem[]>([]);
  const [includeUsed, setIncludeUsed] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const reload = () => api.yutaiItems(includeUsed).then((r) => setItems(r.items)).catch(setError);
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeUsed]);

  const setState = async (item: YutaiItem, state: YutaiItem["state"]) => {
    await api.updateYutaiItem(item.id, { state });
    await reload();
  };

  return (
    <section>
      <h1>優待</h1>
      <label className="check">
        <input type="checkbox" checked={includeUsed} onChange={(e) => setIncludeUsed(e.target.checked)} /> 使用済みも表示
      </label>
      <ErrorText error={error} />
      <AddItemForm onAdded={reload} />
      {items.map((item) => {
        const left = daysUntil(item.expiresOn);
        const links = searchLinks(item);
        return (
          <div className={`card ${left !== null && left <= 30 && item.state !== "used" ? "highlight" : ""}`} key={item.id}>
            <div className="row between">
              <h2>
                {item.stockCode} {item.stockName ?? ""}
              </h2>
              <span className={`badge ${item.state}`}>{stateLabel[item.state]}</span>
            </div>
            <p>{item.description}</p>
            <dl className="dl">
              <dt>権利確定日</dt>
              <dd>{item.recordDate}</dd>
              {item.state === "waiting" && (
                <>
                  <dt>到着目安</dt>
                  <dd>{item.expectedArrival ?? "-"}</dd>
                </>
              )}
              <dt>使用期限</dt>
              <dd>
                {item.expiresOn ?? "未設定"}
                {left !== null && item.state !== "used" && <span className={left < 0 ? "warn" : "muted"}> （{left < 0 ? `${-left}日超過` : `あと${left}日`}）</span>}
              </dd>
            </dl>
            <div className="row wrap">
              {item.state === "waiting" && (
                <button className="btn" type="button" onClick={() => setState(item, "on_hand")}>
                  到着した
                </button>
              )}
              {item.state !== "used" && (
                <button className="btn primary" type="button" onClick={() => setState(item, "used")}>
                  使用済みにする
                </button>
              )}
              {item.state === "used" && (
                <button className="btn" type="button" onClick={() => setState(item, "on_hand")}>
                  手元に戻す
                </button>
              )}
              <button
                className="btn"
                type="button"
                onClick={async () => {
                  const v = prompt("使用期限 (YYYY-MM-DD)", item.expiresOn ?? "");
                  if (v === null) return;
                  await api.updateYutaiItem(item.id, { expiresOn: v || null });
                  await reload();
                }}
              >
                期限を編集
              </button>
              <a className="btn link" href={links.yahoo} target="_blank" rel="noopener noreferrer">
                ヤフオクで探す
              </a>
              <a className="btn link" href={links.mercari} target="_blank" rel="noopener noreferrer">
                メルカリで探す
              </a>
              <button
                className="btn small danger"
                type="button"
                onClick={async () => {
                  if (!confirm("この優待を削除しますか？")) return;
                  await api.deleteYutaiItem(item.id);
                  await reload();
                }}
              >
                削除
              </button>
            </div>
          </div>
        );
      })}
      {items.length === 0 && <p className="muted">優待はまだありません。権利付最終日を過ぎると所持銘柄とクロス実績から自動で追加されます。</p>}
    </section>
  );
}

function AddItemForm({ onAdded }: { onAdded: () => Promise<unknown> }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [recordDate, setRecordDate] = useState("");
  const [description, setDescription] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [arrived, setArrived] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await api.createYutaiItem({
        stockCode: code.toUpperCase(),
        stockName: name || undefined,
        recordDate,
        description,
        expiresOn: expiresOn || null,
        arrivedOn: arrived ? today : null,
      });
      setOpen(false);
      setCode("");
      setName("");
      setDescription("");
      setExpiresOn("");
      await onAdded();
    } catch (err) {
      setError(err);
    }
  };

  if (!open) {
    return (
      <button className="btn" type="button" onClick={() => setOpen(true)}>
        手動で追加
      </button>
    );
  }
  return (
    <form className="card form" onSubmit={submit}>
      <Field label="証券コード">
        <input className="input" required pattern="[0-9][0-9A-Za-z]{3}" value={code} onChange={(e) => setCode(e.target.value)} />
      </Field>
      <Field label="会社名（任意）">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="権利確定日">
        <input className="input" type="date" required value={recordDate} onChange={(e) => setRecordDate(e.target.value)} />
      </Field>
      <Field label="内容">
        <input className="input" required value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label="使用期限（任意）">
        <input className="input" type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
      </Field>
      <label className="check">
        <input type="checkbox" checked={arrived} onChange={(e) => setArrived(e.target.checked)} /> すでに手元にある
      </label>
      <ErrorText error={error} />
      <div className="row">
        <button className="btn primary" type="submit">
          追加
        </button>
        <button className="btn" type="button" onClick={() => setOpen(false)}>
          キャンセル
        </button>
      </div>
    </form>
  );
}
